import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/services/auth.service';
import { ChangePasswordService } from '../services/change-password.service';
import { LoginService } from '../../login/services/login.service';
import { AppSettingsService } from '../../../core/services/app-settings.service';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly changePasswordService = inject(ChangePasswordService);

  protected readonly isSubmitting = signal(false);
  protected readonly serverError = signal<string | null>(null);
  private readonly appSettingsService = inject(AppSettingsService);
  protected readonly hideCurrentPassword = signal(true);
  protected readonly hideNewPassword = signal(true);
  protected readonly hideConfirmPassword = signal(true);
  private readonly loginService = inject(LoginService);
  protected readonly form = this.fb.nonNullable.group(
    {
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
    },
    {
      validators: [
        this.passwordsMatchValidator,
        this.newPasswordDifferentFromCurrentValidator,
      ],
    }
  );

  protected submit(): void {
    this.serverError.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    this.changePasswordService
      .changePassword({
        currentPassword: this.form.controls.currentPassword.value,
        newPassword: this.form.controls.newPassword.value,
      })
      .subscribe({
        next: (response) => {
          this.authService.updateTokens(response);

          this.authService.hydrateRuntimePermissions().subscribe({
            next: () => {
              this.isSubmitting.set(false);

              this.appSettingsService.setSidebarCollapsed(false);

              this.router.navigate([this.authService.getFirstAllowedRoute()]);
            },
            error: () => {
              this.isSubmitting.set(false);
              this.serverError.set(
                'Die Berechtigungen konnten nicht geladen werden. Bitte melden Sie sich erneut an.'
              );
            },
          });
        },
        error: () => {
          this.isSubmitting.set(false);
          this.serverError.set(
            'Das Passwort konnte nicht geändert werden. Bitte überprüfen Sie Ihr aktuelles Passwort.'
          );
        },
      });
  }

  protected logout(): void {
    const refreshToken = this.authService.getRefreshToken();

    if (!refreshToken) {
      this.authService.clearSession();
      this.router.navigate(['/login']);
      return;
    }

    this.loginService.logout(refreshToken).subscribe({
      next: () => {
        this.authService.clearSession();
        this.router.navigate(['/login']);
      },
      error: () => {
        this.authService.clearSession();
        this.router.navigate(['/login']);
      },
    });
  }

  protected toggleCurrentPasswordVisibility(): void {
    this.hideCurrentPassword.update((value) => !value);
  }

  protected toggleNewPasswordVisibility(): void {
    this.hideNewPassword.update((value) => !value);
  }

  protected toggleConfirmPasswordVisibility(): void {
    this.hideConfirmPassword.update((value) => !value);
  }

  private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (!newPassword || !confirmPassword) {
      return null;
    }

    return newPassword === confirmPassword ? null : { passwordsNotMatching: true };
  }

  private newPasswordDifferentFromCurrentValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const currentPassword = control.get('currentPassword')?.value;
    const newPassword = control.get('newPassword')?.value;

    if (!currentPassword || !newPassword) {
      return null;
    }

    return currentPassword === newPassword
      ? { newPasswordSameAsCurrent: true }
      : null;
  }
}